/**
 * Contexto de audio único de la app. En Safari 15 el audio solo arranca tras un toque, y el
 * modo silencio del iPad apaga Web Audio salvo que haya un elemento <audio> sonando (§9.5).
 */
import { SILENCE_MP3 } from './media'

type AudioContextConstructor = typeof AudioContext

let context: AudioContext | null = null
let sessionAudio: HTMLAudioElement | null = null

function audioContextConstructor(): AudioContextConstructor {
  const legacy = (window as Window & { webkitAudioContext?: AudioContextConstructor })
    .webkitAudioContext
  const Constructor = window.AudioContext ?? legacy
  if (!Constructor) throw new Error('Este navegador no tiene Web Audio')
  return Constructor
}

/** El contexto de audio si ya se creó; no lo crea (crearlo antes de un toque no sirve en iOS). */
export function peekAudioContext(): AudioContext | null {
  return context
}

export function getAudioContext(): AudioContext {
  if (!context) {
    context = new (audioContextConstructor())({ latencyHint: 'interactive' })
    context.addEventListener('statechange', () => {
      console.info(`AudioContext: ${context?.state}`)
    })
  }
  return context
}

/**
 * Desbloquea el audio. Hay que llamarla dentro del gestor de un toque: iOS exige que el primer
 * sonido empiece en ese mismo instante, por eso el búfer mudo se lanza antes de esperar a nada.
 */
export async function unlockAudio(): Promise<AudioContext> {
  const ctx = getAudioContext()
  const buffer = ctx.createBuffer(1, 1, ctx.sampleRate)
  const source = ctx.createBufferSource()
  source.buffer = buffer
  source.connect(ctx.destination)
  source.start(0)
  if (ctx.state !== 'running') await ctx.resume()
  return ctx
}

/**
 * Con un <audio> mudo en bucle, iOS pasa la sesión de audio a «reproducción» y Web Audio
 * suena aunque el iPad esté en silencio. También se llama desde un toque.
 */
export async function setPlaybackSession(enabled: boolean): Promise<boolean> {
  if (!enabled) {
    sessionAudio?.pause()
    return false
  }
  if (!sessionAudio) {
    sessionAudio = document.createElement('audio')
    sessionAudio.src = SILENCE_MP3
    sessionAudio.loop = true
    sessionAudio.preload = 'auto'
    sessionAudio.setAttribute('playsinline', '')
    sessionAudio.setAttribute('x-webkit-airplay', 'deny')
  }
  try {
    await sessionAudio.play()
    return true
  } catch (error) {
    console.warn('No se pudo iniciar la sesión de reproducción', error)
    return false
  }
}

export function isPlaybackSessionActive(): boolean {
  return sessionAudio !== null && !sessionAudio.paused
}

export interface AudioTiming {
  sampleRate: number
  baseLatency: number | null
  outputLatency: number | null
  outputTimestamp: boolean
}

export function audioTiming(ctx: AudioContext): AudioTiming {
  const extended = ctx as AudioContext & { outputLatency?: number; baseLatency?: number }
  return {
    sampleRate: ctx.sampleRate,
    baseLatency: typeof extended.baseLatency === 'number' ? extended.baseLatency : null,
    outputLatency: typeof extended.outputLatency === 'number' ? extended.outputLatency : null,
    outputTimestamp: typeof ctx.getOutputTimestamp === 'function',
  }
}

export interface OutputTimestampSample {
  currentTime: number
  contextTime: number | null
  performanceTime: number | null
  performanceNow: number
}

/** Muestra cruda de getOutputTimestamp para diagnosticar navegadores que la implementan mal. */
export function sampleOutputTimestamp(ctx: AudioContext): OutputTimestampSample {
  const stamp = typeof ctx.getOutputTimestamp === 'function' ? ctx.getOutputTimestamp() : null
  return {
    currentTime: ctx.currentTime,
    contextTime: stamp?.contextTime ?? null,
    performanceTime: stamp?.performanceTime ?? null,
    performanceNow: performance.now(),
  }
}

/** Diferencia máxima admitida entre getOutputTimestamp y currentTime antes de descartarla. */
const MAX_OUTPUT_ESTIMATE_GAP = 0.5

export interface HeardTime {
  time: number
  source: 'outputTimestamp' | 'currentTime'
}

/**
 * Instante del audio que está sonando ahora mismo por el altavoz. Con getOutputTimestamp se
 * descuenta la latencia de salida; si no existe, se aproxima con la latencia conocida.
 *
 * En el iPad (Safari 15) el resaltado no avanzaba: getOutputTimestamp existe pero da valores
 * que no cuadran con currentTime. Solo se usa si su estimación queda cerca de currentTime.
 */
export function heardTimeWithSource(ctx: AudioContext): HeardTime {
  const timing = audioTiming(ctx)
  const fallback = ctx.currentTime - (timing.outputLatency ?? timing.baseLatency ?? 0)
  if (typeof ctx.getOutputTimestamp === 'function') {
    const stamp = ctx.getOutputTimestamp()
    if (
      typeof stamp.contextTime === 'number' &&
      typeof stamp.performanceTime === 'number' &&
      stamp.performanceTime > 0
    ) {
      const estimate = stamp.contextTime + (performance.now() - stamp.performanceTime) / 1000
      if (Math.abs(estimate - ctx.currentTime) < MAX_OUTPUT_ESTIMATE_GAP) {
        return { time: estimate, source: 'outputTimestamp' }
      }
    }
  }
  return { time: fallback, source: 'currentTime' }
}

export function heardTime(ctx: AudioContext): number {
  return heardTimeWithSource(ctx).time
}

/**
 * Retraso entre programar un sonido y percibirlo, en segundos: el calibrado si lo hay (incluye el
 * altavoz y la reacción de quien toca) y, si no, la estimación del navegador (§5.2).
 */
export function perceivedDelay(ctx: AudioContext, calibratedMs: number | undefined): number {
  if (calibratedMs !== undefined) return calibratedMs / 1000
  return Math.max(0, ctx.currentTime - heardTime(ctx))
}

/** Instante del reloj de audio en que ocurrió un toque o una tecla, a partir de su `timeStamp`. */
export function audioTimeOfEvent(ctx: AudioContext, timeStamp: number): number {
  const now = performance.now()
  // Si la marca no está en la escala de performance.now() (navegadores antiguos), se usa «ahora».
  const stamp = timeStamp > 0 && Math.abs(now - timeStamp) < 10_000 ? timeStamp : now
  return ctx.currentTime - Math.max(0, now - stamp) / 1000
}
